import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
    ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    FormBuilder,
    Validators,
    FormControl,
    ReactiveFormsModule
} from '@angular/forms';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import {
    MatAutocompleteModule,
    MatAutocompleteSelectedEvent,
    MatAutocompleteTrigger
} from '@angular/material/autocomplete';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatDatepickerModule } from '@angular/material/datepicker';
import {
    MatNativeDateModule,
    MAT_DATE_LOCALE
} from '@angular/material/core';
import {
    debounceTime,
    distinctUntilChanged,
    switchMap,
    map,
    startWith,
    catchError,
    of,
    take,
    takeUntil,
    tap,
    Subject
} from 'rxjs';

import { PlantsService } from '../services/plants.service';
import { CityService } from '../services/city.service';
import { PlantLocationComponent } from '../add-plant/plant-location-component/plant-location-component.component';
import { UploadPlantImageComponent } from '../add-plant/upload-plant-image/upload-plant-image.component';
import {
    ReferenceIdsComponent,
    ExternalReference
} from '../reference-ids/reference-ids.component';
import {
    cityIdFromAutocompleteValidator,
    latitudeValidator,
    longitudeValidator,
    locationValidator
} from '../shared/validators';

import { PlantDto } from '../Models/plantDto.interface';
import { SelectedLocation } from '../Models/selectLocation.interface';
import { AddNewCityModalComponent } from '../add-plant/add-new-city-modal/add-new-city-modal.component';
import { Location } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import {
    installationDateFromString,
    installationDateToString
} from '../shared/installation-date.util';

type CityOption = { id: number; title: string; timeZoneId: string };

@Component({
    selector: 'app-update-plant',
    standalone: true,
    imports: [
        CommonModule,
        RouterModule,
        ReactiveFormsModule,
        MatCardModule,
        MatButtonModule,
        MatFormFieldModule,
        MatInputModule,
        MatIconModule,
        MatSelectModule,
        MatAutocompleteModule,
        MatProgressSpinnerModule,
        MatDialogModule,
        PlantLocationComponent,
        UploadPlantImageComponent,
        ReferenceIdsComponent,
        TranslatePipe,
        MatDatepickerModule,
        MatNativeDateModule,
    ],
    providers: [
        {
            provide: MAT_DATE_LOCALE,
            useValue: 'en-GB'
        }
    ],
    templateUrl: './update-plant.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: [
        './update-plant.component.scss',
        '../add-plant/add-plant.component.scss'
    ]
})
export class UpdatePlantComponent implements OnInit {
    private destroy$ = new Subject<void>();
    plantId!: number;
    isSubmitting = false;
    loading = true;
    priceClass: any[] = [{ priceClass: 'DK1' }, { priceClass: 'DK2' }];
    customerTypeOptions: string[] = [];
    externalServiceNames: string[] = [];
    citySearchCtrl = new FormControl<string>('', { nonNullable: true });
    filteredCities$ = of([] as CityOption[]);
    selectedCity?: CityOption;
    uploadedImageUrl: string | null = null;
    fileHandle: string | null = null;
    @ViewChild(UploadPlantImageComponent) fileHold?: UploadPlantImageComponent;
    @ViewChild(MatAutocompleteTrigger)
    private cityAutoTrigger?: MatAutocompleteTrigger;
    @ViewChild('cityInput', { read: ElementRef })
    private cityInput?: ElementRef<HTMLInputElement>;

    private patchingCity = false;
    readonly today = new Date();
    constructor(
        private fb: FormBuilder,
        private plantsSvc: PlantsService,
        private route: ActivatedRoute,
        private sb: MatSnackBar,
        private router: Router,
        private dialog: MatDialog,
        private cdr: ChangeDetectorRef,
        private cityService: CityService,
        private location: Location
    ) { }

    personalForm = this.fb.group({
        plantOwner: ['', [Validators.required, Validators.minLength(3)]],
        email: ['', [Validators.email, Validators.required]],
        phone: ['', Validators.required],
        address: ['', Validators.required],
        cityId: this.fb.control<number | null>(null, [
            Validators.required,
            cityIdFromAutocompleteValidator(() => this.selectedCity)
        ]),
        username: ['', Validators.required],
        userId: this.fb.control<number | null>(null),
        priceClass: this.fb.control<string | null>(null, [Validators.required]),
        lowPriceThreshold: this.fb.control<string | null>(null, [
            Validators.required
        ]),
        customerType: this.fb.control<string | null>(null, [
            Validators.required
        ]),
        plantImageUrl: this.fb.control<string | null>(null),
        location: this.fb.control<SelectedLocation | null>(null, [
            locationValidator()
        ]),
        latitude: this.fb.control<number | null>(null, [
            Validators.required,
            latitudeValidator()
        ]),
        longitude: this.fb.control<number | null>(null, [
            Validators.required,
            longitudeValidator()
        ]),
        timeZoneId: this.fb.control<string | null>(null),
        externalReferences: this.fb.control<ExternalReference[]>([], {
            nonNullable: true
        }),
        installationDate: this.fb.control<Date | null>(null),
    });

    get isLocked(): boolean {
        return false;
    }

    ngOnInit(): void {
        this.loadPrerequisites();

        this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
            const id = Number(params.get('id'));
            if (!id) return;

            this.plantId = id;

            this.initCityAutocomplete();
            this.bindLocationProjection();

            this.loadPlantAndPatch(id);
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private bindLocationProjection() {
        const locCtrl = this.personalForm.get('location')!;
        const latCtrl = this.personalForm.get('latitude')!;
        const lngCtrl = this.personalForm.get('longitude')!;

        const project = (loc: SelectedLocation | null) => {
            if (
                loc &&
                typeof loc.lat === 'number' &&
                typeof loc.lng === 'number'
            ) {
                latCtrl.setValue(+loc.lat.toFixed(6), { emitEvent: false });
                lngCtrl.setValue(+loc.lng.toFixed(6), { emitEvent: false });
            } else {
                latCtrl.reset(null, { emitEvent: false });
                lngCtrl.reset(null, { emitEvent: false });
            }
            latCtrl.updateValueAndValidity({
                onlySelf: true,
                emitEvent: false
            });
            lngCtrl.updateValueAndValidity({
                onlySelf: true,
                emitEvent: false
            });
        };

        project(locCtrl.value as any);

        locCtrl.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(v => project(v as any));
    }

    private initCityAutocomplete() {
        this.filteredCities$ = this.citySearchCtrl.valueChanges.pipe(
            startWith(this.citySearchCtrl.value ?? ''),
            debounceTime(250),
            distinctUntilChanged(),
            switchMap(q => {
                const query = (q ?? '').trim();
                return this.plantsSvc.getCities(query).pipe(
                    map(res =>
                        (res?.cities ?? []).map(
                            (c: any) =>
                            ({
                                id: Number(c.id),
                                title: c.title ?? '',
                                timeZoneId: c.timeZoneId ?? ''
                            } as CityOption)
                        )
                    ),
                    catchError(err => {
                        console.error('getCities failed', err);
                        return of([] as CityOption[]);
                    })
                );
            })
        );

        this.citySearchCtrl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    if (this.patchingCity) return;

                    const typed = (this.citySearchCtrl.value ?? '').trim();
                    if (!typed) {
                        this.selectedCity = undefined;
                        this.personalForm.patchValue(
                            { cityId: null, timeZoneId: null },
                            { emitEvent: false }
                        );
                        this.personalForm
                            .get('cityId')
                            ?.updateValueAndValidity({ emitEvent: false });
                        this.personalForm
                            .get('timeZoneId')
                            ?.updateValueAndValidity({ emitEvent: false });
                        return;
                    }

                    if (
                        this.selectedCity &&
                        typed !== this.selectedCity.title
                    ) {
                        this.selectedCity = undefined;
                        this.personalForm.patchValue(
                            { cityId: null, timeZoneId: null },
                            { emitEvent: false }
                        );
                        this.personalForm
                            .get('cityId')
                            ?.updateValueAndValidity({ emitEvent: false });
                        this.personalForm
                            .get('timeZoneId')
                            ?.updateValueAndValidity({ emitEvent: false });
                    }
                })
            )
            .subscribe();
    }

    onCityOptionSelected(ev: MatAutocompleteSelectedEvent) {
        const city = ev.option.value as CityOption;
        this.selectedCity = city;

        this.patchingCity = true;
        this.citySearchCtrl.setValue(city.title, { emitEvent: false });
        this.personalForm.patchValue(
            { cityId: city.id, timeZoneId: city.timeZoneId || null },
            { emitEvent: false }
        );
        this.patchingCity = false;

        this.personalForm
            .get('cityId')
            ?.updateValueAndValidity({ emitEvent: false });
        this.personalForm
            .get('timeZoneId')
            ?.updateValueAndValidity({ emitEvent: false });
        this.personalForm.updateValueAndValidity({ emitEvent: false });
    }

    private loadPrerequisites() {
        this.plantsSvc
            .getAddInverterPrerequisites()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.customerTypeOptions = (res?.customerTypes ?? [])
                        .map((ct: any) => ct.title)
                        .filter(Boolean);
                },
                error: (err: any) =>
                    console.error('getAddInverterPrerequisites failed', err)
            });

        this.plantsSvc
            .getAddPlantPrerequisites()
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    this.externalServiceNames = res?.externalServiceName ?? [];
                },
                error: (err: any) =>
                    console.error('getAddPlantPrerequisites failed', err)
            });
    }

    private loadPlantAndPatch(id: number) {
        this.loading = true;
        this.plantsSvc
            .getPlantFullDetail(id)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: any) => {
                    const p = res?.plantInfo ?? res?.personal ?? res ?? null;
                    if (!p) {
                        this.loading = false;
                        return;
                    }

                    const patch: any = {
                        plantOwner: p.plantOwner ?? p._plantOwner ?? '',
                        email: p.email ?? p._email ?? '',
                        username: p.userName ?? p.username ?? p._userName ?? '',
                        phone: p.phoneNumber ?? p.phone ?? p._phoneNumber ?? '',
                        address: p.address ?? p.Address ?? p._address ?? '',
                        userId: p.userId ?? p._userId ?? null,
                        installationDate: installationDateFromString(
                            p.installationDate ??
                            p._installationDate ??
                            null
                        ),
                        priceClass: p.priceClass ?? p._priceClass ?? null,
                        lowPriceThreshold: (
                            p.lowPriceThreshold ??
                            p._lowPriceThreshold ??
                            ''
                        ).toString(),
                        customerType: p.customerType ?? p._customerType ?? null,
                        externalReferences: (p.externalReferences ??
                            []) as ExternalReference[]
                    };

                    if (p.latitude != null && p.longitude != null) {
                        patch.location = {
                            lat: Number(p.latitude),
                            lng: Number(p.longitude)
                        };
                        patch.latitude = Number(p.latitude);
                        patch.longitude = Number(p.longitude);
                    }
                    const cityId = p.cityId ?? p._cityId ?? null;
                    const cityTitle = p.cityName ?? null;
                    this.patchingCity = true;
                    if (cityId != null) {
                        const cid = Number(cityId);
                        patch.cityId = cid;
                        const title = (cityTitle ?? '').toString();
                        const tzFromPlant = (p.timeZoneId ?? '').toString();
                        this.selectedCity = {
                            id: cid,
                            title,
                            timeZoneId: tzFromPlant
                        };
                        if (title)
                            this.citySearchCtrl.setValue(title, {
                                emitEvent: false
                            });
                        if (!tzFromPlant) {
                            this.cityService
                                .getTimeZoneIdByCityId(cid)
                                .pipe(take(1))
                                .subscribe(tz => {
                                    const tzId = (tz ?? '').toString();

                                    this.selectedCity = {
                                        id: cid,
                                        title,
                                        timeZoneId: tzId
                                    };
                                    this.personalForm.patchValue(
                                        {
                                            timeZoneId: tzId || null,
                                            cityId: cid
                                        },
                                        { emitEvent: false }
                                    );

                                    this.personalForm
                                        .get('timeZoneId')
                                        ?.updateValueAndValidity({
                                            emitEvent: false
                                        });
                                    this.personalForm
                                        .get('cityId')
                                        ?.updateValueAndValidity({
                                            emitEvent: false
                                        });
                                    this.personalForm.updateValueAndValidity({
                                        emitEvent: false
                                    });

                                    this.patchingCity = false;
                                    this.cdr.detectChanges();
                                });
                        } else {
                            patch.timeZoneId = tzFromPlant;
                        }
                    } else {
                        patch.cityId = null;
                        patch.timeZoneId = null;
                        this.selectedCity = undefined;
                        this.citySearchCtrl.setValue('', { emitEvent: false });
                    }

                    this.personalForm.patchValue(patch, { emitEvent: false });
                    this.personalForm
                        .get('cityId')
                        ?.updateValueAndValidity({ emitEvent: false });
                    this.personalForm
                        .get('timeZoneId')
                        ?.updateValueAndValidity({ emitEvent: false });
                    this.personalForm.updateValueAndValidity({
                        emitEvent: false
                    });

                    this.patchingCity = false;
                    this.loading = false;

                    try {
                        this.cdr.detectChanges();
                    } catch { }
                },
                error: (err: any) => {
                    this.loading = false;
                    console.error('getPlantFullDetail failed', err);
                    this.notify(
                        err?.statusMessage ?? 'Failed to load plant',
                        'error'
                    );
                }
            });
    }

    onClickUpdatePlant() {
        if (this.selectedCity && !this.personalForm.get('cityId')?.value) {
            this.personalForm.patchValue(
                {
                    cityId: this.selectedCity.id,
                    timeZoneId: this.selectedCity.timeZoneId || null
                },
                { emitEvent: false }
            );
            this.personalForm
                .get('cityId')
                ?.updateValueAndValidity({ emitEvent: false });
            this.personalForm
                .get('timeZoneId')
                ?.updateValueAndValidity({ emitEvent: false });
            this.personalForm.updateValueAndValidity({ emitEvent: false });
        }

        if (this.personalForm.invalid) {
            this.personalForm.markAllAsTouched();
            this.notify('Please complete the required fields.', 'warn');
            return;
        }

        if (!this.plantId || this.plantId <= 0) {
            this.notify('PlantId not set — cannot update plant', 'error');
            return;
        }

        const dto = this.buildPlantDtoFromForms();
        const cityId = this.personalForm.get('cityId')?.value ?? 0;

        const lowPriceThresholdRaw = this.personalForm.get('lowPriceThreshold')
            ?.value;
        const lowPriceThreshold =
            lowPriceThresholdRaw && lowPriceThresholdRaw !== ''
                ? lowPriceThresholdRaw.toString()
                : '0';

        const priceClass = this.personalForm.get('priceClass')?.value ?? '';
        const customerType = this.personalForm.get('customerType')?.value ?? '';

        this.isSubmitting = true;

        this.plantsSvc
            .updatePlant(this.plantId, dto, {
                cityId,
                lowPriceThreshold,
                priceClass,
                customerType
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: () => {
                    this.isSubmitting = false;
                    this.notify('Plant updated successfully.', 'success');
                    this.router.navigate([
                        '/plants/plants-detail',
                        this.plantId
                    ]);
                },
                error: (err: any) => {
                    this.isSubmitting = false;
                    this.notify(
                        err?.statusMessage ?? 'UpdatePlant failed',
                        'error'
                    );
                }
            });
    }

    private buildPlantDtoFromForms(): PlantDto {
        const p = this.personalForm.getRawValue() as any;
        const loc = this.personalForm.get('location')?.value as any;

        return {
            plantOwner: p.plantOwner,
            email: p.email || undefined,
            phoneNumber: p.phone || undefined,
            address: p.address || undefined,
            userId: p.userId || undefined,
            cityId: p.cityId || undefined,
            cityName: p.cityName || undefined,
            latitude: loc?.lat ?? undefined,
            longitude: loc?.lng ?? undefined,
            username: p.username,
            priceClass: p.priceClass || undefined,
            customerType: p.customerType || undefined,
            lowPriceThreshold: (p.lowPriceThreshold || undefined).toString(),
            plantImageUrl: this.fileHandle || undefined,
            externalReferences: (p.externalReferences ??
                []) as ExternalReference[],
            installationDate: installationDateToString(
                p.installationDate
            ),
        };
    }

    onFileHandleReady(fileHandle: string) {
        this.fileHandle = fileHandle;
    }

    onImageUploaded(event: { url: string; fileHandle: string }) {
        this.uploadedImageUrl = event.url;
        this.fileHandle = event.fileHandle;
        this.personalForm.patchValue(
            { plantImageUrl: event.fileHandle },
            { emitEvent: false }
        );
    }

    onCancel() {
        this.router.navigate(['/plants/plants-detail', this.plantId]);
    }

    openAddCity(e: MouseEvent) {
        e.preventDefault();
        e.stopPropagation();

        this.cityAutoTrigger?.closePanel();
        this.cityInput?.nativeElement.blur();

        const ref = this.dialog.open(AddNewCityModalComponent, {
            width: '720px',
            maxWidth: '92vw',
            height: 'auto',
            maxHeight: '90vh',
            autoFocus: false,
            restoreFocus: false,
            panelClass: ['city-dialog', 'elevated-dialog'],
            backdropClass: 'dim-backdrop'
        });

        ref.afterClosed()
            .pipe(takeUntil(this.destroy$))
            .subscribe(
                (city?: { id: number; title: string; timeZoneId: string }) => {
                    if (!city) return;

                    this.selectedCity = city;
                    this.patchingCity = true;

                    this.citySearchCtrl.setValue(city.title, {
                        emitEvent: false
                    });
                    this.personalForm.patchValue(
                        {
                            cityId: city.id,
                            timeZoneId: city.timeZoneId || null
                        },
                        { emitEvent: false }
                    );

                    this.patchingCity = false;

                    this.personalForm
                        .get('cityId')
                        ?.updateValueAndValidity({ emitEvent: false });
                    this.personalForm
                        .get('timeZoneId')
                        ?.updateValueAndValidity({ emitEvent: false });
                    this.personalForm.updateValueAndValidity({
                        emitEvent: false
                    });
                }
            );
    }

    private notify(
        msg: string,
        type: 'success' | 'error' | 'warn' | 'info' = 'info',
        duration = 8000
    ) {
        const cls =
            type === 'success'
                ? 'snack-success'
                : type === 'error'
                    ? 'snack-error'
                    : type === 'warn'
                        ? 'snack-warn'
                        : 'snack-info';

        this.sb.open(msg, 'Close', {
            duration,
            horizontalPosition: 'right',
            verticalPosition: 'top',
            panelClass: [cls]
        });
    }

    goBack() {
        this.location.back();
    }
    clearInstallationDate(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();

        this.personalForm
            .get('installationDate')
            ?.setValue(null);

        this.personalForm
            .get('installationDate')
            ?.markAsDirty();
    }
}
